import type { AnalyticsProps, AnalyticsValue } from "./types.js";

const EMAIL_REGEX = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;
// US-style phone numbers with optional area-code parens and separators.
// Matches "(555) 123-4567", "555-123-4567", "555.123.4567", "+1 555 123 4567".
// The leading country-code group is itself optional so we don't consume the
// leading space when the number starts with `(`.
const PHONE_REGEX = /(?:\+?\d[\s-])?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g;
const SSN_REGEX = /\b\d{3}-?\d{2}-?\d{4}\b/g;
const PII_PROP_KEYS = new Set([
  "email",
  "phone",
  "first_name",
  "firstName",
  "last_name",
  "lastName",
  "name",
  "ssn",
  "social_security_number",
  "dob",
  "date_of_birth",
  "dateOfBirth",
  "address",
  "address1",
  "address2",
  "street",
  "city",
  "state",
  "zip",
  "postal_code",
  "postalCode",
]);

const TRUNCATE_LENGTH = 200;

function scrubString(value: string): string {
  return value
    .replace(EMAIL_REGEX, "[redacted-email]")
    .replace(PHONE_REGEX, "[redacted-phone]")
    .replace(SSN_REGEX, "[redacted-ssn]")
    .slice(0, TRUNCATE_LENGTH);
}

function scrubValue(value: AnalyticsValue): AnalyticsValue {
  if (typeof value === "string") return scrubString(value);
  return value;
}

/**
 * Two layers of PII defence: drop keys that are PII by name (no
 * `setOrganizationContext.email` carve-out — that path lives in
 * `setOrganizationContext`, not in `track()`), then scrub residual
 * email/phone/SSN patterns out of remaining string values.
 *
 * The `event` rule already forbids putting PII on events, so this is
 * the safety net, not the primary defence. Two layers per
 * `cross-cutting/analytics.mdc`.
 */
export function stripPII(props: AnalyticsProps): Readonly<Record<string, AnalyticsValue>> {
  const out: Record<string, AnalyticsValue> = {};
  for (const [key, raw] of Object.entries(props)) {
    if (PII_PROP_KEYS.has(key)) continue;
    out[key] = scrubValue(raw);
  }
  return out;
}
